import { useParams, Link } from "react-router-dom";
import { projects } from "../data/projects";
import { projectContent } from "../data/projectContent";
import InterestForm from "../components/InterestForm";

const STATUS_COLOR: Record<string, string> = {
  live: "var(--status-live)",
  beta: "var(--status-beta)",
  building: "var(--status-building)",
  dormant: "var(--status-dormant)",
};

export default function ProjectInfoPage() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find((p) => p.id === slug);

  if (!project) {
    return (
      <div style={containerStyle}>
        <div style={{ ...innerStyle, textAlign: "center", paddingTop: "6rem" }}>
          <h1 style={{ fontFamily: "var(--mono)", color: "var(--accent)", fontSize: "1.5rem" }}>
            project not found
          </h1>
          <p style={{ color: "var(--fg-dim)", marginBottom: "2rem" }}>
            nothing here by that name.
          </p>
          <Link to="/?view=cosmos" style={backLinkStyle}>← back to the constellation</Link>
        </div>
      </div>
    );
  }

  const content = projectContent[project.id];

  return (
    <div style={containerStyle}>
      <div style={innerStyle}>
        <nav style={navStyle}>
          <Link to="/?view=cosmos" style={backLinkStyle}>← brainsteam labs</Link>
        </nav>

        <section style={heroStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
            <span
              style={{
                fontFamily: "var(--mono)",
                fontSize: "0.7rem",
                color: STATUS_COLOR[project.status],
                background: "rgba(255,255,255,0.04)",
                padding: "0.25rem 0.55rem",
                border: `1px solid ${STATUS_COLOR[project.status]}55`,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {project.status}
            </span>
          </div>
          <h1 style={titleStyle}>{project.name}</h1>
          {content?.tagline && (
            <p style={taglineStyle}>{content.tagline}</p>
          )}
          <p style={blurbStyle}>{project.blurb}</p>
          {project.tags.length > 0 && (
            <div style={tagsContainerStyle}>
              {project.tags.map((tag) => (
                <span key={tag} style={tagStyle}>{tag}</span>
              ))}
            </div>
          )}
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              style={visitStyle}
            >
              visit {project.name.toLowerCase()} →
            </a>
          )}
        </section>

        {content?.features && content.features.length > 0 && (
          <section style={featuresContainerStyle}>
            {content.features.map((f) => (
              <div key={f.title} style={cardStyle}>
                <h3 style={cardTitleStyle}>{f.title}</h3>
                <p style={cardBodyStyle}>{f.body}</p>
              </div>
            ))}
          </section>
        )}

        {content?.steps && content.steps.length > 0 && (
          <section style={stepsContainerStyle}>
            <h2 style={sectionHeadingStyle}>How it works</h2>
            <ol style={stepListStyle}>
              {content.steps.map((step, i) => (
                <li key={i} style={stepItemStyle}>
                  <span style={stepNumberStyle}>{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
          </section>
        )}

        <section style={formSectionStyle}>
          <h2 style={sectionHeadingStyle}>Interested?</h2>
          <p style={{ color: "var(--fg-dim)", marginBottom: "1.5rem", textAlign: "center" }}>
            Leave your email and a note. It goes straight to a GitHub issue — no mailing list, no spam.
          </p>
          <InterestForm project={project.id} projectName={project.name} />
        </section>

        <footer style={footerStyle}>
          <span>
            Built by{" "}
            <Link to="/" style={{ color: "var(--accent)" }}>
              BrainSteam Labs
            </Link>
          </span>
        </footer>
      </div>
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  minHeight: "100%",
  overflowY: "auto",
  background: "var(--bg)",
};

const innerStyle: React.CSSProperties = {
  maxWidth: "780px",
  margin: "0 auto",
  padding: "0 1.5rem",
};

const navStyle: React.CSSProperties = {
  padding: "1.5rem 0",
};

const backLinkStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: "0.8125rem",
  color: "var(--fg-dim)",
  textDecoration: "none",
};

const heroStyle: React.CSSProperties = {
  padding: "3rem 0 2rem",
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: "clamp(2rem, 5vw, 3rem)",
  fontWeight: 700,
  color: "var(--accent)",
  margin: "0 0 0.5rem",
  letterSpacing: "-0.02em",
};

const taglineStyle: React.CSSProperties = {
  fontFamily: "var(--sans)",
  fontSize: "clamp(1.125rem, 2.5vw, 1.375rem)",
  color: "var(--fg)",
  margin: "0 0 1rem",
  fontWeight: 500,
};

const blurbStyle: React.CSSProperties = {
  fontFamily: "var(--sans)",
  fontSize: "1rem",
  lineHeight: 1.7,
  color: "var(--fg-dim)",
  margin: "0 0 1.5rem",
  maxWidth: "600px",
};

const tagsContainerStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.4rem",
  marginBottom: "1.5rem",
};

const tagStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: "0.7rem",
  color: "var(--fg-dim)",
  border: "1px solid rgba(255,255,255,0.12)",
  padding: "0.2rem 0.5rem",
  letterSpacing: "0.04em",
};

const visitStyle: React.CSSProperties = {
  display: "inline-block",
  fontFamily: "var(--mono)",
  fontSize: "0.875rem",
  color: "var(--accent)",
  border: "1px solid var(--accent)",
  padding: "0.6rem 1rem",
  textDecoration: "none",
};

const featuresContainerStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "1rem",
  padding: "2rem 0",
};

const cardStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "12px",
  padding: "1.25rem",
};

const cardTitleStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: "0.9375rem",
  fontWeight: 600,
  color: "var(--fg)",
  margin: "0 0 0.5rem",
};

const cardBodyStyle: React.CSSProperties = {
  fontFamily: "var(--sans)",
  fontSize: "0.875rem",
  color: "var(--fg-dim)",
  lineHeight: 1.6,
  margin: 0,
};

const stepsContainerStyle: React.CSSProperties = {
  padding: "2rem 0",
  maxWidth: "600px",
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "var(--fg)",
  textAlign: "center",
  marginBottom: "1.5rem",
};

const stepListStyle: React.CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: "0.75rem",
};

const stepItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.875rem",
  padding: "1rem 1.25rem",
  background: "rgba(255,255,255,0.03)",
  borderRadius: "10px",
  fontFamily: "var(--sans)",
  fontSize: "0.9375rem",
  color: "var(--fg)",
};

const stepNumberStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  background: "var(--accent)",
  color: "var(--bg)",
  borderRadius: "50%",
  fontWeight: 700,
  fontSize: "0.8125rem",
  flexShrink: 0,
};

const formSectionStyle: React.CSSProperties = {
  padding: "3rem 0",
  maxWidth: "480px",
  margin: "0 auto",
};

const footerStyle: React.CSSProperties = {
  padding: "2rem 0 3rem",
  textAlign: "center",
  fontFamily: "var(--mono)",
  fontSize: "0.75rem",
  color: "var(--fg-dim)",
  borderTop: "1px solid rgba(255,255,255,0.05)",
};
